import { FormControl, FormHelperText } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} props.label - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param {boolean} [props.required] - The required state of the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string} [props.helperText] - The error message of the input field
 * @param { import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/material/Checkbox').CheckboxProps} [props.checkBoxProps] - The props for the text field
 * @param {Omit<import('@mui/material/FormControlLabel').FormControlLabelProps, 'label' | 'control'>} [props.formControlLabelProps] - The props for the form control label
 */

const ControlledCheckBoxComponent = ({
  name,
  control,
  label = '',
  required = false,
  controllerOptions = {},
  checkBoxProps = {},
  formControlLabelProps = {},
  error = false,
  helperText = '',
}) => {
  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                required={required}
                size="small"
                {...checkBoxProps}
              />
            }
            label={label}
            required={required}
            {...formControlLabelProps}
          />
          {error ? <FormHelperText error>{helperText}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
};

ControlledCheckBoxComponent.displayName = 'ControlledCheckBox';

const ControlledCheckBox = memo(ControlledCheckBoxComponent);

export default ControlledCheckBox;
